/**
 * THE ARRIVAL FRAGMENT: an opaque URL fragment the portal carries across the
 * sign-in hop, client-side only, and puts back on the destination URL.
 *
 * Why it exists (2026-09-20, the Revent first-touch e2e run). Revent puts a
 * scannable code on screen that encodes `/?app=revent#c=<code>&n=<name>&e=<event>`.
 * Scanned while signed out, the link is redirected to
 * `/login?signup=1&returnTo=%2F%3Fapp%3Drevent`, and the browser re-attaches the
 * fragment to that redirect, so it reaches the login screen intact. It died
 * there: the login screen built its provider links from the query string
 * alone, the click on one of them started a navigation with no fragment, and
 * the OAuth round trip landed the person on their workspace holding nothing.
 * The same link opened while already signed in redeemed with zero taps, and a
 * brand-new account created with the fragment on the sign-up URL redeemed too,
 * so this one hop was the single break in an otherwise working chain.
 *
 * Three rules, each the shape of a way this could go wrong:
 *
 *   1. THE FRAGMENT NEVER REACHES THE SERVER. It is a live one-use code. It is
 *      read from `location.hash`, parked in `sessionStorage` (per tab, per
 *      origin, gone with the tab; not a cookie, not a query parameter, not
 *      `localStorage`) and re-appended by the destination page. Nothing here
 *      logs it, renders it, or sends it anywhere. (Since 2026-10-03 the
 *      sign-in screen draws ONE thing out of it, the inviter's name, and
 *      only when it reads as a person's name: lib/auth/login-app.ts
 *      `inviterNameFromFragment`, SECURITY_ISSUES_LOG.md S-393. The code,
 *      the card reference and the event are still never drawn.)
 *   2. IT IS HOSTILE INPUT AT BOTH ENDS. Only the bounded, allowlisted grammar
 *      below is carried; a fragment that does not match is dropped, never
 *      repaired or passed through. The read side validates what it finds in
 *      storage all over again, because storage is writable by any script on
 *      the origin, and the app that finally receives it validates once more.
 *   3. IT CANNOT MOVE THE DESTINATION. A stash is written only next to a
 *      `returnTo` that passes `isSafeReturnPath`, and what comes back out is
 *      appended after `#` to whatever same-origin path the sign-in callbacks
 *      already chose. The accepted alphabet has no `#`, `?`, `/`, `\`, `=`
 *      outside the key separator, `%` outside a two-hex-digit escape, no
 *      whitespace and no control character, so the string can only ever be a
 *      fragment: it cannot become a path, a query, a second fragment or
 *      another origin, and it cannot be mistaken for HTML.
 *
 * THE GRAMMAR, in full:
 *
 *   fragment   := pair ("&" pair)*          one to five pairs, no key twice
 *   pair       := key "=" value
 *   key        := "c" | "r" | "n" | "e" | "f"
 *   value      := (unreserved | pct)+       at least one atom
 *   unreserved := ALPHA | DIGIT | "-" | "_" | "." | "!" | "~" | "*" | "'" | "(" | ")"
 *   pct        := "%" HEXDIG HEXDIG
 *   folder     := 8LHEX "-" 4LHEX "-" 4LHEX "-" 4LHEX "-" 12LHEX   the value of "f", and only of "f"
 *   LHEX       := DIGIT | "a" | "b" | "c" | "d" | "e" | "f"           lower case, the way a folder id is spelled
 *   whole fragment, without the "#", at most 512 characters
 *
 * `value` is exactly the output alphabet of `encodeURIComponent`, which is how
 * the emitter writes every value it does not already confine to a fixed
 * alphabet, so a name such as O'Brien (the apostrophe is unreserved) is
 * carried and `+`, a raw space, a second `=`, or any byte outside ASCII is
 * refused. The keys are the portal's arrival vocabulary today: `c` a one-use
 * code, `r` a card reference, `n` a display name (a string to draw, never an
 * identity), `e` the event or entity it was shown at, `f` the folder of the
 * conference that event lives in (Revent's share link for an event of a
 * conference somebody made, 2026-09-25: the reader has been given access to
 * the folder, and the app has to know which folder to read before it can open
 * the event). `f` is the one key with a shape of its own on top of `value`,
 * because a folder id is one thing and nothing else is a folder id; the check
 * is a shape, not a lookup, so the portal still interprets nothing. Adding a
 * key is a deliberate edit to `ARRIVAL_FRAGMENT_KEYS` (and to `KEY_SHAPES` when
 * the value has a fixed spelling) AND a validation in the app that reads it:
 * the portal carries a fragment, it never interprets one.
 *
 * Plain module with no server-only imports, like `return-path.ts`: the login
 * screen and the restore component are both client code.
 */

import { isSafeReturnPath } from './return-path';

/** The fixed key vocabulary. See the module header before adding to it. */
export const ARRIVAL_FRAGMENT_KEYS: ReadonlySet<string> = new Set(['c', 'r', 'n', 'e', 'f']);
/**
 * A key whose value has a fixed spelling on top of the `value` class. `f` is a
 * conference folder id: a lower-case uuid, checked as a shape only.
 */
const KEY_SHAPES: Readonly<Record<string, RegExp>> = {
  f: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/,
};
/** Whole fragment, without the `#`. Matches the `returnTo` cap in return-path.ts. */
export const ARRIVAL_FRAGMENT_MAX_LEN = 512;
/** sessionStorage key. One slot: a later arrival replaces an earlier one. */
export const ARRIVAL_FRAGMENT_STORAGE_KEY = 'rb_arrival_fragment';
/**
 * How long a parked fragment may wait for the sign-in to complete. Long enough
 * for a brand-new provider account with a second factor; short enough that a
 * login abandoned at lunch does not decorate an unrelated sign-in later.
 */
export const ARRIVAL_FRAGMENT_TTL_MS = 15 * 60_000;

/* The `value` production: encodeURIComponent's unreserved set, or `%XX`. */
const VALUE_RE = /^(?:[A-Za-z0-9\-_.!~*'()]|%[0-9A-Fa-f]{2})+$/;
/* Refuse to even parse a storage record much larger than any legitimate one. */
const MAX_RECORD_LEN = ARRIVAL_FRAGMENT_MAX_LEN + 64;

/**
 * The minimal Storage surface used here, so tests can hand in a Map-backed
 * fake and so callers pass `window.sessionStorage` in explicitly (reading
 * that property throws inside an opaque-origin frame; the caller owns that
 * try/catch).
 */
export interface ArrivalFragmentStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * The fragment, without its leading `#`, when `hash` is exactly the grammar
 * above; null for anything else. Never repairs, never partially accepts: one
 * bad pair drops the whole fragment.
 */
export function parseArrivalFragment(hash: unknown): string | null {
  if (typeof hash !== 'string') return null;
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw || raw.length > ARRIVAL_FRAGMENT_MAX_LEN) return null;
  const pairs = raw.split('&');
  if (pairs.length > ARRIVAL_FRAGMENT_KEYS.size) return null;
  const seen = new Set<string>();
  for (const pair of pairs) {
    const eq = pair.indexOf('=');
    // `eq < 1`: no separator at all, or an empty key.
    if (eq < 1) return null;
    const key = pair.slice(0, eq);
    const value = pair.slice(eq + 1);
    if (!ARRIVAL_FRAGMENT_KEYS.has(key) || seen.has(key)) return null;
    // Also refuses an empty value and any second `=`, neither is in the class.
    if (!VALUE_RE.test(value)) return null;
    // A key with its own spelling (`f`, a folder id) is held to it as well.
    const shape = KEY_SHAPES[key];
    if (shape && !shape.test(value)) return null;
    seen.add(key);
  }
  return raw;
}

/**
 * Park the fragment for the destination page, on the login screen. Writes only
 * when the fragment matches the grammar AND `returnTo` is a same-origin path
 * by the one shared rule (`isSafeReturnPath`): a fragment with no return
 * address, or with one that fails the rule, is dropped, so the stash can never
 * exist without a destination the callbacks would have accepted anyway.
 * Returns the parked fragment, or null when nothing was written.
 */
export function stashArrivalFragment(input: {
  hash: unknown;
  returnTo: unknown;
  storage: ArrivalFragmentStore | null | undefined;
  now?: number;
}): string | null {
  const fragment = parseArrivalFragment(input.hash);
  if (!fragment) return null;
  if (!isSafeReturnPath(input.returnTo)) return null;
  if (!input.storage) return null;
  try {
    input.storage.setItem(
      ARRIVAL_FRAGMENT_STORAGE_KEY,
      JSON.stringify({ f: fragment, at: input.now ?? Date.now() }),
    );
  } catch {
    // Quota, private mode, blocked storage: the fragment is simply not carried.
    return null;
  }
  return fragment;
}

/** What `prepareSignInHandoff` worked out. `href` is always a `/login` URL. */
export interface SignInHandoff {
  /** Where the caller should send the person. Same-origin path, always `/login`. */
  href: string;
  /** The page the sign-in callbacks will come back to, or null when this page's own path failed the rule. */
  returnTo: string | null;
  /** The fragment actually parked, or null when nothing was carried. */
  carried: string | null;
}

/**
 * THE CLIENT-SIDE SIGN-IN HOP (2026-09-20, the Revent first-touch runs).
 *
 * Everything above this line assumes the hop that gets a person to `/login` is
 * a SERVER REDIRECT, because that is the one case the first Revent run covered:
 * `/?app=revent#c=…` is redirected to `/login?returnTo=…`, the redirect itself
 * carries no fragment of its own, and so the browser re-attaches the original
 * one and the login screen can park it. Two surfaces do not work that way, and
 * both lost the code:
 *
 *   - the `/w/` share surface's "Sign in to save" control
 *     (`components/widgets/standalone-widget.tsx`) is a `next/link` click, so
 *     the new URL is written by `history.pushState` from an href this code
 *     builds. A fragment the person arrived with is not re-attached to
 *     anything; it is simply dropped, and `/login` never sees a hash to park.
 *     That is the exact path a scanned stranger takes.
 *   - `ui.signIn` (`components/workspace/widget-bridge.tsx`), the host op an
 *     app calls to ask for sign-in, navigates the top-level document itself
 *     for the same reason: a sandboxed app cannot do it and cannot frame a
 *     provider.
 *
 * So these two park the fragment BEFORE they navigate, and it never goes in the
 * URL at all. That is strictly better than putting it back on the href: a live
 * one-use code has no business in an address bar, a referrer, or a history
 * entry, and a client-side navigation could not be trusted to carry it anyway.
 *
 * `/login` IS THE DESTINATION, not a provider start route, and this function
 * can return nothing else. A screen, because the host does not know which
 * identity this person has: sending everyone to Google strands whoever signed
 * up with Discord, X, or a passkey, and `/login` is the only surface that
 * renders all of them plus the passkey path, the account-picker flag, and the
 * provider-error re-entry. It also keeps the provider list in exactly one
 * place instead of a second copy inside the bridge.
 *
 * WHAT IT WILL NOT DO. The caller hands in the page it is ON (`pathname`,
 * `search`) and never a destination, so there is no argument here through
 * which anything could aim the redirect somewhere else. That path is checked
 * with `isSafeReturnPath` regardless of who passed it, and a path that fails
 * yields a bare `/login` with NO stash: `stashArrivalFragment` refuses to park
 * next to an unsafe `returnTo`, so the failure mode is losing the code, never
 * moving the person. The query it builds has exactly one parameter.
 */
export function prepareSignInHandoff(input: {
  /** THIS page's path. Never a caller-chosen destination. */
  pathname: unknown;
  /** THIS page's query, kept whole so a share link's own `?t=` token survives. */
  search: unknown;
  /** The fragment to carry, re-validated here against the grammar. */
  carry: unknown;
  storage: ArrivalFragmentStore | null | undefined;
  now?: number;
}): SignInHandoff {
  const pathname = typeof input.pathname === 'string' ? input.pathname : '';
  const search = typeof input.search === 'string' ? input.search : '';
  const candidate = `${pathname}${search}`;
  // Our own location, and checked anyway: the cost of being wrong here is an
  // open redirect off a page somebody trusted enough to sign in from.
  const returnTo = isSafeReturnPath(candidate) ? candidate : null;
  if (!returnTo) return { href: '/login', returnTo: null, carried: null };
  const carried = stashArrivalFragment({
    hash: input.carry,
    returnTo,
    storage: input.storage,
    now: input.now,
  });
  return { href: `/login?returnTo=${encodeURIComponent(returnTo)}`, returnTo, carried };
}

/**
 * Take the parked fragment, once. The slot is cleared on the first read
 * whatever it held, so a record is redeemed by exactly one page. Anything
 * older than the TTL, from the future, malformed, oversized or outside the
 * grammar comes back as null: storage is not trusted just because we wrote it.
 */
export function takeArrivalFragment(
  storage: ArrivalFragmentStore | null | undefined,
  now: number = Date.now(),
): string | null {
  if (!storage) return null;
  let raw: string | null = null;
  try {
    raw = storage.getItem(ARRIVAL_FRAGMENT_STORAGE_KEY);
    if (raw !== null) storage.removeItem(ARRIVAL_FRAGMENT_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw || raw.length > MAX_RECORD_LEN) return null;
  let record: unknown;
  try {
    record = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!record || typeof record !== 'object') return null;
  const { f, at } = record as { f?: unknown; at?: unknown };
  if (typeof at !== 'number' || !Number.isFinite(at)) return null;
  const age = now - at;
  if (age < 0 || age > ARRIVAL_FRAGMENT_TTL_MS) return null;
  return parseArrivalFragment(f);
}
